/**
 * Fire-and-forget wake-up pings for the backends the sign-in path needs.
 *
 * The Container Apps behind Mission Control (Zitadel, the BFF) can scale to
 * zero, and the sign-in flow otherwise reaches them strictly one after the
 * other: Zitadel only on the SIGN IN click, the BFF only once the client
 * picker mounts after the callback. Pinging both while the user is still
 * looking at the login screen overlaps their cold starts with think-time.
 *
 * The Zitadel ping goes through the UserManager's metadata service, so the
 * discovery document it fetches is cached and reused by signinRedirect()
 * instead of being fetched again on click.
 *
 * Failures are swallowed — these are hints, never a gate.
 */

import { env, isZitadelMode } from "@/env";
import { getUserManager } from "./user-manager";

const BFF_HEALTH_URL = `${env.VITE_DOCS_BFF_ORIGIN.replace(/\/+$/, "")}/api/bff/health`;

let idpWarmed = false;
let bffWarmed = false;

/** Wake Zitadel and prime the cached OIDC discovery document. */
export function warmUpIdp(): void {
  if (idpWarmed || !isZitadelMode()) return;
  idpWarmed = true;
  void getUserManager()
    .metadataService.getMetadata()
    .catch(() => {
      // Let signinRedirect() retry discovery (and surface any error) itself.
      idpWarmed = false;
    });
}

/** Wake the BFF via its unauthenticated health probe. */
export function warmUpBff(): void {
  if (bffWarmed || !isZitadelMode()) return;
  bffWarmed = true;
  void fetch(BFF_HEALTH_URL, { cache: "no-store" }).catch(() => {
    bffWarmed = false;
  });
}

/** Test-only: reset the once-per-page-load guards. */
export function __resetWarmUpForTests(): void {
  idpWarmed = false;
  bffWarmed = false;
}
